<script lang="ts">
	import { querystring } from 'svelte-spa-router';
	//Stores
	import { home_data, user_data, user_settings } from 'src/stores/account/user';
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	//Design system components
	import {
		ButtonV2,
		Text,
		toast,
		InputV2,
		Icon,
		Container,
		IconButton,
		AlertV2,
		Stack,
	} from '@Shelly/shelly-design-system';
	import { delete_home, save_home } from 'src/helpers/home_helpers';
	import { isMultiHome, getInstallerCodes, removeInstallerCode } from 'src/helpers/installer_helpers';
	import RevokeConfirmationModal from './RevokeConfirmationModalV2.svelte';
	import { onMount } from 'svelte';
	import CountdownTimer from './CountdownTimer.svelte';
	import { timestampToDate } from 'src/helpers/date_helper';
	import GrantInstallerInfoModal from './GrantInstallerInfoModalV2.svelte';
	import GrantInstallerAccessModal from './GrantInstallerAccessModalV2.svelte';

	$: userId = $user_data?.user_id;
	$: multiHome = isMultiHome($user_data);
	$: installerCodes = getInstallerCodes($user_settings?.icodes, userId) as string[];

	let installerCode = '';
	let installerData: { [key: string]: string } = {};
	let revokeReqLoading = false;
	let revokeShowModal = false;
	let addReqLoading = false;
	let showInfoModal = false;
	let showModal = false;
	let codePopover = '';
	let revokedCode = '';
	let message = '';

	// re-grant access variables
	let regrantAccessCode = '';

	const help2Variant = 'help-2' as
		| 'success'
		| 'danger'
		| 'label'
		| 'primary'
		| 'secondary'
		| 'custom'
		| 'danger-secondary'
		| 'help';

	onMount(() => {
		const searchParams = new URLSearchParams($querystring);
		const code = searchParams.get('code');
		if (searchParams.has('installerAccess')) {
			installerCode = code;
			getInstallersData();
		}
	});

	const getInstallersData = async (manual?: boolean) => {
		try {
			const res = await postReq('user/get_installer_info', {
				icode: installerCode,
				env: import.meta.env.VITE_APP_BUILD,
			});

			if (res.isok) {
				installerData = res.data as any;
				const realInstallerCode = installerData?.electrician_code || installerData.code;
				if (
					installerCodes.includes(realInstallerCode) ||
					$home_data?.icodes?.[installerData.code]?.electrician_code ===
						installerData?.electrician_code
				) {
					toast.open({
						type: 'info',
						title: $format('toast_used_installer_code', {
							default: 'You have already used this installer code',
						}),
					});
					if (!manual) installerCode = '';
					return;
				}
				showModal = true;
			} else {
				toast.open({
					type: 'danger',
					title: $format('toast_wrong_installer_code', {
						default: 'Wrong installer code',
					}),
				});
				if (!manual) installerCode = '';
			}
		} catch (err) {
			console.log('Error on the request (Network error) ', err);
			toast.open({ type: 'danger', title: $format('error_global') });
			if (!manual) installerCode = '';
		}
	};

	const revokeAccess = async (code) => {
		revokeReqLoading = true;
		const res = await postReq('user/remove_installer', {
			icode: code,
			env: import.meta.env.VITE_APP_BUILD,
			...(message.trim() && { message }),
		});

		if (res.isok) {
			const { icodes: nextIcodes, userCodes: remainingUserCodes } = removeInstallerCode(
				$user_settings.icodes,
				code,
				multiHome,
				userId,
			);
			user_settings.set({ ...$user_settings, icodes: nextIcodes });
			if (remainingUserCodes.length <= 0) {
				delete_home('icodes');
			} else {
				const newHomeDataIcodes = $home_data.icodes;
				delete newHomeDataIcodes[code];
				save_home('icodes', newHomeDataIcodes);
			}

			if (Object.keys($home_data?.installers_time).length > 1) {
				const newHomeDataInstallersTime = $home_data?.installers_time;
				delete newHomeDataInstallersTime[code];
				save_home('installers_time', newHomeDataInstallersTime);
			} else {
				delete_home('installers_time');
			}
		} else {
			toast.open({
				type: 'danger',
				title: $format('toast_wrong_installer_code', {
					default: 'Wrong installer code',
				}),
			});
		}

		message = '';
		revokedCode = '';
		revokeShowModal = false;
		revokeReqLoading = false;
	};
</script>

<AlertV2
	backgroundColor="color-00"
	class="mb-s"
	heading={$format('alert_info_installer_access', {
		default:
			'Granting access to an installer allows them to work on your home. During this period, you can manually grant access again if needed. You can revoke access at any time.',
	})}
/>

<Text styleType="body-medium" class="mb-s">
	{$format('collapse_title_installer_code', {
		default: 'Installer code',
	})}
</Text>

<Text styleType="body-small" class="mb-s text-color-muted">
	{$format('text_installer_code_description_1', {
		default: 'Enter the provided installer code',
	})}
</Text>

<Stack direction="column" directionMobile="row" align="between" alignMobile="none" gap="s">
	<InputV2
		backgroundColor="color-black"
		label="XXXXXXXX"
		class="mb-s mb-md-0 width-full width-md-auto"
		icon="fa-solid fa-key"
		bind:value={installerCode}
		id="input-fzd8o9k"
		aria-labelledby="input-fzd8o9k"
	/>

	<ButtonV2
		size="m"
		width="s"
		variant="secondary"
		class={`${installerCodes.length > 0 ? 'mb-s' : ''} width-full width-md-auto`}
		disabled={!installerCode || addReqLoading}
		on:click={() => getInstallersData(true)}
	>
		{$format('button_grant_access', { default: 'Grant access' })}
	</ButtonV2>
</Stack>

{#if $home_data?.icodes && installerCodes.length > 0}
	<hr class="mt-s mb-m" />
	<Text styleType="body" class="mb-m">
		{$format('text_installer_code_grant_access', {
			default: 'Installer codes with granted access:',
		})}
	</Text>
	{#each installerCodes as code}
		<Container padding="xs" noScroll class="mb-s installer-code-container">
			<div class="d-flex align-items-center justify-content-between flex-wrap">
				<div class="mb-s d-flex align-items-center">
					<Icon icon="fas fa-user-helmet-safety" size="s" />
					<Text styleType="body-small" class="ml-s text-600">
						{($home_data?.icodes[code]?.electrician_name &&
							`${$home_data?.icodes[code]?.electrician_name} (${$home_data?.icodes[code]?.company_name})`) ||
							$home_data?.icodes[code]?.name ||
							code}
					</Text>
					<IconButton
						aria-label={$format('button_info', { default: 'Info' })}
						class="ml-s"
						variant={help2Variant}
						shape="circle"
						icon="fa-solid fa-circle-info"
						on:click={() => {
							codePopover = code;
							showInfoModal = true;
						}}
					/>
				</div>
				<div class="d-flex align-items-center flex-wrap actions-row">
					{#if $home_data?.installers_time && $home_data?.installers_time[code] <= new Date().getTime()}
						<ButtonV2
							size="m"
							variant="secondary"
							class="mr-s mb-s"
							on:click={() => {
								installerData = $home_data.icodes[code];
								regrantAccessCode = code;
								showModal = true;
							}}
						>
							{$format('button_re_grant', {
								default: 'Grant',
							})}
						</ButtonV2>
					{/if}
					<ButtonV2
						size="m"
						class="mr-s mb-s"
						variant="primary"
						disabled={revokeReqLoading}
						on:click={() => {
							revokedCode = code;
							revokeShowModal = true;
						}}
					>
						{$format('button_revoke', {
							default: 'Revoke',
						})}
					</ButtonV2>
					{#if $home_data?.installers_time && $home_data?.installers_time[code] > new Date().getTime()}
						<div class="countdown-wrapper mb-s">
							<CountdownTimer targetTime={$home_data?.installers_time[code]} />
						</div>
					{/if}
				</div>
			</div>
			{#if $home_data?.installers_time}
				{#if $home_data?.installers_time[code] > new Date().getTime()}
					<Text styleType="body-small" class="text-color-muted">
						{$format('info_installer_access_granted', {
							default: 'Installer access granted until {time}',
							values: {
								time: timestampToDate($home_data?.installers_time[code]),
							},
						})}
					</Text>
				{:else}
					<Text styleType="body-small" class="text-color-muted">
						{$format('heading_warning_installer_access_expired_1', {
							default:
								'Installer access has expired. You can either grant access again or revoke it.',
						})}
					</Text>
				{/if}
			{/if}
		</Container>
	{/each}
{/if}

<GrantInstallerAccessModal
	bind:message
	bind:showModal
	bind:addReqLoading
	bind:installerData
	bind:installerCode
	bind:regrantAccessCode
/>

<GrantInstallerInfoModal bind:showInfoModal bind:codePopover />

<!-- revoke access modal -->
<!-- @ToDo - reword that we are revoking access and removing the installer -->
<RevokeConfirmationModal
	bind:message
	bind:revokeReqLoading
	bind:showModal={revokeShowModal}
	revokeInstallerAccess
	on:revoke={() => revokeAccess(revokedCode)}
	mainText={$format('message_modal_revoke_installers_access', {
		default:
			'Revoking the installer’s access will immediately remove their permissions and prevent them from accessing your home.',
	})}
/>

<!-- @ToDo - add a option to just remove the access will need API @spas and @tomov -->

<style>
	:global(.installer-code-container) {
		background-color: #191a1e !important;
		border: 1px solid #212128 !important;
	}

	.actions-row {
		min-width: 0;
	}

	.countdown-wrapper {
		max-width: 100%;
		overflow: hidden;
	}
</style>
